// AddPropertyModal — pin a candidate property to a client's shortlist.
// Submits to POST /agent/cases/{id}/shortlist via addShortlistItem.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AIHint,
  Btn,
  Chip,
  Field,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalShell,
  TextArea,
  TextInput,
} from "./ModalShell";
import { PropertyAutocomplete, type PropertyHit } from "./PropertyAutocomplete";
import type { AddPropertyCtx } from "./FlowsProvider";
import { addShortlistItem, type CreateShortlistItemInput } from "@/lib/shortlistApi";

// Values stay English — they persist to case_shortlist_items.tags and drive
// existing rows. The label is localised for display via TAG_LABEL_KEY.
const TAGS = ["Stretch budget", "Best value", "High floor", "Rare unit", "Backup"];
const TAG_LABEL_KEY: Record<string, string> = {
  "Stretch budget": "agent.flows.addProperty.tagOpt.stretchBudget",
  "Best value": "agent.flows.addProperty.tagOpt.bestValue",
  "High floor": "agent.flows.addProperty.tagOpt.highFloor",
  "Rare unit": "agent.flows.addProperty.tagOpt.rareUnit",
  Backup: "agent.flows.addProperty.tagOpt.backup",
};

type Form = {
  projectQuery?: string;
  selected?: PropertyHit;
  address?: string;
  price?: string;
  size?: string;
  beds?: string;
  reason?: string;
  tags?: string[];
};

// Parses a free-text money / size field into a positive number.
// Strips currency symbols, commas, and unit suffixes ("sqft"). Returns
// undefined for empty / unparseable / non-positive input so the field
// is omitted from the payload rather than sent as a placeholder 0.
function parseNumeric(s?: string): number | undefined {
  if (!s) return undefined;
  const n = parseFloat(s.replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function AddPropertyModal({
  open,
  ctx,
  onSaved,
  onClose,
}: {
  open: boolean;
  ctx: AddPropertyCtx | null;
  onSaved?: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<Form>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({});
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    setError(null);
    const caseId = ctx?.caseObj?.id;
    if (!caseId) {
      setError(
        t("agent.flows.addProperty.needCase", {
          defaultValue: "Open this from a client case to add a property.",
        }) as string,
      );
      return;
    }
    if (!form.selected) return;
    const input: CreateShortlistItemInput = {
      project_name: form.selected.name,
      property_kind: form.selected.kind,
      address: form.address?.trim() ?? "",
      price: parseNumeric(form.price),
      size_sqft: parseNumeric(form.size),
      beds: form.beds?.trim() ?? "",
      why: form.reason?.trim() ?? "",
      tags: form.tags ?? [],
    };
    setSubmitting(true);
    try {
      await addShortlistItem(caseId, input);
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelect = (hit: PropertyHit) => {
    setForm((f) => ({
      ...f,
      selected: hit,
      projectQuery: hit.name,
      // Autofill address from the hit. For HDB this is "Blk X Street", for
      // projects it's the street; postal is appended when present.
      address: composeAddress(hit),
    }));
  };

  const clearSelection = () =>
    setForm((f) => ({ ...f, selected: undefined, projectQuery: "" }));

  return (
    <ModalShell open={open} onClose={onClose} width={640} label="Add property">
      <ModalHeader
        eyebrow={t("agent.flows.addProperty.eyebrow", { defaultValue: "Add to shortlist" })}
        title={t("agent.flows.addProperty.title", { defaultValue: "New property for the client" })}
        hint={
          ctx?.caseObj?.shortName ? (
            <>
              {t("agent.flows.for", { defaultValue: "For" })}{" "}
              <span style={{ color: "var(--ag-accent)", fontWeight: 600 }}>
                {ctx.caseObj.shortName}
              </span>
            </>
          ) : (
            t("agent.flows.addProperty.hint", {
              defaultValue: "Pin a candidate property to this client's shortlist.",
            })
          )
        }
        onClose={onClose}
      />

      <ModalBody>
        <Field
          label={t("agent.flows.addProperty.project", { defaultValue: "Project / HDB block" })}
          hint={t("agent.flows.addProperty.projectHint", {
            defaultValue: "Type to search private projects and HDB blocks.",
          })}
        >
          {form.selected ? (
            <SelectedHitChip hit={form.selected} onClear={clearSelection} />
          ) : (
            <PropertyAutocomplete
              value={form.projectQuery ?? ""}
              onChange={(v) => setForm((f) => ({ ...f, projectQuery: v }))}
              onSelect={handleSelect}
              placeholder="e.g. The Marina Collection, Blk 123 Tampines"
              autoFocus
            />
          )}
        </Field>

        {form.selected && <PropertyMetaRow hit={form.selected} />}

        <Field label={t("agent.flows.addProperty.address", { defaultValue: "Address" })}>
          <TextInput
            value={form.address}
            onChange={(v) => setForm((f) => ({ ...f, address: v }))}
            placeholder="Street, postal code"
          />
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Field label={t("agent.flows.addProperty.asking", { defaultValue: "Asking" })}>
            <TextInput
              mono
              value={form.price}
              onChange={(v) => setForm((f) => ({ ...f, price: v }))}
              placeholder="$1,450,000"
            />
          </Field>
          <Field label={t("agent.flows.addProperty.size", { defaultValue: "Size" })}>
            <TextInput
              mono
              value={form.size}
              onChange={(v) => setForm((f) => ({ ...f, size: v }))}
              placeholder="743 sqft"
            />
          </Field>
          <Field label={t("agent.flows.addProperty.beds", { defaultValue: "Bedrooms" })}>
            <TextInput
              value={form.beds}
              onChange={(v) => setForm((f) => ({ ...f, beds: v }))}
              placeholder="1+Study"
            />
          </Field>
        </div>
        <Field
          label={t("agent.flows.addProperty.reason", { defaultValue: "Why it fits" })}
          hint={t("agent.flows.addProperty.reasonHint", {
            defaultValue: "Helps the AI explain it to the client.",
          })}
        >
          <TextArea
            value={form.reason}
            onChange={(v) => setForm((f) => ({ ...f, reason: v }))}
            rows={2}
            placeholder="e.g. MRT 4 min, freehold, fits client's freehold preference."
          />
        </Field>
        <Field label={t("agent.flows.addProperty.tag", { defaultValue: "Tag" })}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {TAGS.map((tag) => {
              const active = (form.tags ?? []).includes(tag);
              return (
                <Chip
                  key={tag}
                  active={active}
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      tags: active
                        ? (f.tags ?? []).filter((x) => x !== tag)
                        : [...(f.tags ?? []), tag],
                    }))
                  }
                >
                  {t(TAG_LABEL_KEY[tag] ?? "", { defaultValue: tag })}
                </Chip>
              );
            })}
          </div>
        </Field>
        <AIHint>
          {t("agent.flows.addProperty.ai", {
            defaultValue:
              "We'll score this against the client's brief and surface conflicts before they see it.",
          })}
        </AIHint>
      </ModalBody>

      {error && (
        <div
          style={{
            margin: "0 16px 8px",
            padding: "8px 10px",
            background: "var(--ag-bad-bg, rgba(191,56,56,0.08))",
            border: "1px solid var(--ag-bad, #bf3838)",
            borderRadius: 6,
            color: "var(--ag-bad, #bf3838)",
            fontSize: 12.5,
          }}
        >
          {error}
        </div>
      )}

      <ModalFooter leftHint={t("agent.flows.escCancel", { defaultValue: "Esc to cancel" })}>
        <Btn ghost onClick={onClose} disabled={submitting}>
          {t("agent.flows.cancel", { defaultValue: "Cancel" })}
        </Btn>
        <Btn primary disabled={!form.selected || submitting} onClick={handleSubmit}>
          {submitting
            ? t("agent.flows.addProperty.submitting", { defaultValue: "Adding…" })
            : t("agent.flows.addProperty.submit", { defaultValue: "Add to shortlist" })}
        </Btn>
      </ModalFooter>
    </ModalShell>
  );
}

function SelectedHitChip({ hit, onClear }: { hit: PropertyHit; onClear: () => void }) {
  const badgeLabel = hit.kind === "hdb" ? "HDB" : (hit.property_types?.[0] ?? "Private");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        borderRadius: 8,
        border: "1px solid var(--ag-accent)",
        background: "var(--ag-accent-soft)",
      }}
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.5,
          textTransform: "uppercase",
          padding: "3px 7px",
          borderRadius: 999,
          background: "var(--ag-bg)",
          color: "var(--ag-accent)",
          border: "1px solid var(--ag-accent)",
        }}
      >
        {badgeLabel}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            color: "var(--ag-ink)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {hit.name}
        </div>
        {hit.subtitle && (
          <div style={{ fontSize: 11.5, color: "var(--ag-ink-3)" }}>{hit.subtitle}</div>
        )}
      </span>
      <button
        type="button"
        onClick={onClear}
        aria-label="Change selection"
        style={{
          background: "transparent",
          border: "1px solid var(--ag-rule-strong)",
          borderRadius: 6,
          padding: "4px 8px",
          fontSize: 11,
          color: "var(--ag-ink-2)",
          cursor: "pointer",
        }}
      >
        Change
      </button>
    </div>
  );
}

function PropertyMetaRow({ hit }: { hit: PropertyHit }) {
  const items: { label: string; value: string }[] = [];
  if (hit.kind === "project") {
    if (hit.district) items.push({ label: "District", value: hit.district });
    if (hit.tenure) items.push({ label: "Tenure", value: hit.tenure });
    if (hit.property_types && hit.property_types.length > 0)
      items.push({ label: "Type", value: hit.property_types.join(", ") });
    if (hit.market_segment) items.push({ label: "Segment", value: hit.market_segment });
  } else {
    items.push({ label: "Type", value: "HDB" });
    if (hit.estate_name) items.push({ label: "Estate", value: hit.estate_name });
    if (hit.planning_area) items.push({ label: "Planning area", value: hit.planning_area });
  }
  if (hit.postal) items.push({ label: "Postal", value: hit.postal });
  if (items.length === 0) return null;
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        padding: "8px 10px",
        borderRadius: 8,
        background: "var(--ag-surface-2)",
        border: "1px solid var(--ag-rule)",
      }}
    >
      {items.map((it) => (
        <span
          key={it.label}
          style={{
            fontSize: 11.5,
            color: "var(--ag-ink-2)",
            display: "inline-flex",
            alignItems: "baseline",
            gap: 5,
          }}
        >
          <span
            style={{
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: "var(--ag-ink-3)",
            }}
          >
            {it.label}
          </span>
          <span style={{ fontWeight: 600 }}>{it.value}</span>
        </span>
      ))}
    </div>
  );
}

function composeAddress(hit: PropertyHit): string {
  const parts: string[] = [];
  if (hit.kind === "hdb" && hit.block && hit.street_name) {
    parts.push(`Blk ${hit.block} ${hit.street_name}`);
  } else if (hit.street) {
    parts.push(hit.street);
  }
  if (hit.postal) parts.push(`Singapore ${hit.postal}`);
  return parts.join(", ");
}
